'use client';

import { c as _c } from "react/compiler-runtime";
import { convictionFraction } from '@morfi/backend-sdk-v2/client';
import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { ChampionIconSolid } from '@/components/ui/icon/glyphs';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { Link } from '@/i18n/navigation';
import { formatPercentValue } from '@/lib/format/format-number';
import { MOTION_ENTRANCE, MOTION_EXIT, MOTION_POSITION } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
import { AnimatedOutcomePercent } from './animated-outcome-percent';
import type { OutcomeRowSummary } from './outcome-row-summary';
export interface OutcomeStackProps {
  pubkey: string;
  outcomes: readonly OutcomeRowSummary[];
  consensusThreshold: string | bigint | null;
  winnerOriginalIndex?: number | undefined;
  linkDisabled?: boolean;
  onSelectOutcome?: ((originalIndex: number) => void) | undefined;
}
export function OutcomeStack(t0) {
  const $ = _c(30);
  const {
    pubkey,
    outcomes,
    consensusThreshold,
    winnerOriginalIndex,
    linkDisabled: t1,
    onSelectOutcome
  } = t0;
  const linkDisabled = t1 === undefined ? false : t1;
  const reduceMotion = useReducedMotion();
  const t = useTranslations("market.row");
  let t2;
  if ($[0] !== outcomes) {
    t2 = outcomes.map(_temp);
    $[0] = outcomes;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const colors = useOutcomeColor(t2);
  let T0;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[2] !== colors || $[3] !== consensusThreshold || $[4] !== linkDisabled || $[5] !== onSelectOutcome || $[6] !== outcomes || $[7] !== pubkey || $[8] !== reduceMotion || $[9] !== t || $[10] !== winnerOriginalIndex) {
    let t8;
    if ($[17] !== colors) {
      t8 = (o_0, i) => [o_0.originalIndex, colors[i]];
      $[17] = colors;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    const colorByIndex = new Map(outcomes.map(t8));
    const sorted = [...outcomes].sort(_temp2);
    let t9;
    if ($[19] !== consensusThreshold) {
      t9 = consensusThreshold === null ? 1 : convictionFraction(consensusThreshold);
      $[19] = consensusThreshold;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    const threshold = t9;
    const visible = sorted.slice(0, 2);
    const decimals = visible.length === 2 && Math.round((visible[0]?.share ?? 0) * 100) === Math.round((visible[1]?.share ?? 0) * 100);
    t6 = "pointer-events-none relative z-10 flex flex-col gap-4";
    t7 = "outcome-stack";
    T0 = AnimatePresence;
    t3 = false;
    t4 = "popLayout";
    t5 = visible.map(outcome => {
      const color = colorByIndex.get(outcome.originalIndex);
      const winner = winnerOriginalIndex === outcome.originalIndex;
      const loser = winnerOriginalIndex !== undefined && !winner;
      const labelValues = {
        outcome: outcome.label,
        share: formatPercentValue(outcome.share * 100)
      };
      const ariaLabel = winner ? t("viewOutcomeWinner", labelValues) : loser ? t("viewOutcomeLoser", labelValues) : t("viewOutcome", labelValues);
      const href = `/markets/${pubkey}?outcome=${String(outcome.originalIndex)}`;
      const label = <><OptimizedImage variant="thumbnail" entity={{
          kind: "outcome",
          marketPubkey: pubkey,
          outcomeIndex: outcome.originalIndex,
          imageUrl: outcome.imageUrl ?? null,
          nsfw: outcome.imageNsfw ?? false,
          hue: color?.hue ?? "blue"
        }} alt="" size="size-8" rounded="rounded-item" preview={false} /><span className="flex min-w-0 flex-1 flex-col gap-1"><span className="flex min-w-0 items-center gap-1 text-heading-xs text-foreground-primary">{winner ? <Icon icon={ChampionIconSolid} className="size-icon-xs text-feedback-success-content" aria-hidden="true" /> : null}<span className="truncate">{outcome.label}</span></span><span className="block h-0.5 w-full overflow-hidden rounded-full"><svg className="block size-full" aria-hidden="true"><m.line x1={2} x2="100%" y1={1} y2={1} transform="translate(-1 0)" strokeWidth={2} strokeLinecap="round" className={cn("stroke-current", winner ? "text-feedback-success-content" : loser ? "text-background-tertiary" : color?.text)} initial={false} animate={{
                pathLength: Math.min(1, Math.max(0, outcome.share / (threshold > 0 ? threshold : 1))),
                opacity: outcome.share > 0 ? 1 : 0
              }} transition={reduceMotion ? {
                duration: 0
              } : MOTION_POSITION} data-testid={`outcome-stack-fill-${String(outcome.originalIndex)}`} /></svg></span></span></>;
      const percentage = <AnimatedOutcomePercent value={outcome.share * 100} decimals={decimals} />;
      const buttonClass = cn("pointer-events-auto w-16 shrink-0 px-2", winner ? "bg-feedback-success-surface text-feedback-success-content hover:bg-feedback-success-surface" : loser ? "bg-background-tertiary text-foreground-tertiary hover:bg-background-tertiary" : [color?.fill, color?.text, color?.hoverFill]);
      return <m.div key={`${pubkey}:${String(outcome.originalIndex)}`} layout={reduceMotion ? false : "position"} initial={reduceMotion ? false : {
        opacity: 0
      }} animate={{
        opacity: loser ? 0.6 : 1
      }} exit={{
        opacity: 0
      }} transition={reduceMotion ? {
        duration: 0
      } : {
        ...MOTION_ENTRANCE,
        layout: MOTION_POSITION,
        opacity: MOTION_EXIT
      }} className="flex items-center gap-3" data-testid={`outcome-stack-row-${String(outcome.originalIndex)}`}>{onSelectOutcome !== undefined ? <AnimatedButton variant="tertiary" className={cn("flex min-w-0 flex-1 items-center gap-3 text-left", "pointer-events-auto h-auto justify-start p-0 hover:bg-transparent")} aria-label={ariaLabel} onClick={() => onSelectOutcome(outcome.originalIndex)} disableHoverScale={true} disableTapScale={true}>{label}</AnimatedButton> : linkDisabled ? <span className="flex min-w-0 flex-1 items-center gap-3 text-left">{label}</span> : <Link href={href} prefetch={false} aria-label={ariaLabel} className={cn("flex min-w-0 flex-1 items-center gap-3 text-left", "pointer-events-auto")}>{label}</Link>}<AnimatedButton variant="secondary" size="sm" disableHoverScale={true} className={buttonClass} asChild={onSelectOutcome === undefined && !linkDisabled} disabled={linkDisabled && onSelectOutcome === undefined} aria-label={ariaLabel} onClick={onSelectOutcome === undefined ? undefined : () => onSelectOutcome(outcome.originalIndex)}>{onSelectOutcome === undefined && !linkDisabled ? <Link href={href} prefetch={false}>{percentage}</Link> : percentage}</AnimatedButton></m.div>;
    });
    $[2] = colors;
    $[3] = consensusThreshold;
    $[4] = linkDisabled;
    $[5] = onSelectOutcome;
    $[6] = outcomes;
    $[7] = pubkey;
    $[8] = reduceMotion;
    $[9] = t;
    $[10] = winnerOriginalIndex;
    $[11] = T0;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    T0 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
  }
  let t8;
  if ($[21] !== T0 || $[22] !== t3 || $[23] !== t4 || $[24] !== t5) {
    t8 = <T0 initial={t3} mode={t4}>{t5}</T0>;
    $[21] = T0;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t6 || $[27] !== t7 || $[28] !== t8) {
    t9 = <div className={t6} data-testid={t7}>{t8}</div>;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
}
function _temp2(a, b) {
  return b.share - a.share || a.originalIndex - b.originalIndex;
}
function _temp(o) {
  return {
    title: o.label,
    index: o.originalIndex
  };
}